import { Card, Row, Col, Modal, Form, Spinner, Button, InputGroup, FormLabel, OverlayTrigger, Tooltip } from 'react-bootstrap';
import React, { useState, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/es';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { FaMapMarkerAlt, FaPlayCircle, FaSearch, FaFileExport, FaAngleRight, FaAngleLeft, FaAngleDoubleRight, FaAngleDoubleLeft } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import iconoPng from '../../../../../assets/img/icons/export.png';
import { generateExcelReport } from '../../../../../helpers/createExcelReport';

// Activamos el soporte para formatos personalizados
dayjs.extend(customParseFormat);
dayjs.locale('es');

// Función para convertir a Pascal Case
const toPascalCase = (str) => {
    console.log("toPascalCase input:", str, "tipo:", typeof str);
    if (!str || typeof str !== 'string') return 'N/A';
    const result = str.trim()
        .toLowerCase()
        .split(' ')
        .filter(word => word.length > 0) // Filtrar palabras vacías
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    console.log("toPascalCase result:", result);
    return result || 'Sin Categoría';
};

// const formatFecha = (fechaStr) => {
//     if (!fechaStr || typeof fechaStr !== 'string') return ' ';

//     try {
//         return dayjs(fechaStr, 'YYYY/MM/DD/ HH:mm:ss').format('DD/MM/YYYY hh:mm A');
//     } catch (error) {
//         console.error('Error al formatear la fecha:', error);
//         return 'N/A';
//     }
// };

const formatFecha = (fechaStr) => {
    if (!fechaStr || typeof fechaStr !== 'string') return ' ';

    try {
        let fecha;

        // ✅ DETECTAR FORMATO AUTOMÁTICAMENTE
        if (fechaStr.includes('T')) {
            // Formato ISO: "2025-05-27T20:48:34" o "2025-05-27T20:48:34.000Z"
            fecha = dayjs(fechaStr);
        } else if (fechaStr.includes('/') && fechaStr.includes(' ')) {
            // Formato personalizado: "2025/05/27/ 20:48:34"
            fecha = dayjs(fechaStr, 'YYYY/MM/DD/ HH:mm:ss');
        } else if (fechaStr.includes('-') && fechaStr.includes(' ')) {
            // Formato guión: "2025-05-27 20:48:34"
            fecha = dayjs(fechaStr, 'YYYY-MM-DD HH:mm:ss');
        } else {
            // Parseo automático para otros formatos
            fecha = dayjs(fechaStr);
        }

        // ✅ VERIFICAR VALIDEZ
        if (!fecha.isValid()) {
            console.warn('[formatFecha] Fecha inválida:', fechaStr);
            return 'Fecha inválida';
        }

        // ✅ FORMATEAR (PRESERVA LA ZONA HORARIA LOCAL)
        return fecha.format('DD/MM/YYYY HH:mm:ss');

    } catch (error) {
        console.error('[formatFecha] Error al formatear:', fechaStr, error);
        return 'N/A';
    }
};

const ROW_HEIGHT = 65;
const HEADER_HEIGHT = 40;

const customStyles = `
.list-striped .alert-row:nth-child(odd) {
  background-color: rgba(250, 250, 250, 0.9);
}
.list-striped .alert-row:nth-child(even) {
  background-color: white;
}
.list-striped .alert-row:hover {
  background-color: rgba(245, 245, 245, 1);
}
.table-header {
  background-color: var(--falcon-200);
  font-weight: 600;
  font-size: 0.83rem;
  position: sticky;
  top: 0;
  z-index: 1;
}
.alert-row {
  padding: 0.5rem 0.5rem;
  font-size: 0.83rem;
  transition: background-color 0.15s ease;
}
.avatar-name.rounded-circle {
  display: flex;
  align-items: center;
  justify-content: center;
}
/* Estilos personalizados para tooltips adaptativos */
.tooltip-adaptive {
  pointer-events: auto !important;
}
.tooltip-adaptive .tooltip-inner {
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
  text-align: left !important;
  max-height: 400px !important;
  overflow-y: auto !important;
  min-width: 200px !important;
  max-width: 600px !important;
  padding: 8px 12px !important;
  font-size: 11px !important;
  line-height: 1.3 !important;
}
.tooltip-adaptive .tooltip-arrow {
  display: block !important;
}
`;

const BitacoraTableListTanstack = ({
    data = [],
    columns = [],
    searchValue = '',
    onSearch,
    pageSize = 100,
    onPageSizeChange,
    isLoading = false,
    title = 'Listado de bitácoras',
    isGroup = false,
    isExporting = false,
    onExport,
    onTableRendered
}) => {

    console.log("[BitacotaTableListTanstack] Render: data.length =", data?.length);

    // Verificar si la empresa NO es 9 para ocultar categorías
    const empresaId = localStorage.getItem('empresa_id');
    const shouldHideCategories = empresaId !== '9';

    // Estados para la tabla
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState(searchValue);
    const [inputValue, setInputValue] = useState(searchValue);
    const debounceTimeout = useRef(null);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: pageSize,
    });

    // Sincronizar el filtro global con el searchValue recibido por props
    React.useEffect(() => {
        setGlobalFilter(searchValue);
    }, [searchValue]);

    // Sincronizar el tamaño de página con el pageSize recibido por props
    React.useEffect(() => {
        setPagination(prev => ({
            ...prev,
            pageSize
        }));
    }, [pageSize]);

    React.useEffect(() => {
        if (typeof onTableRendered === 'function') {
            onTableRendered(data.length);
        }
    }, [data.length]);


    const table = useReactTable({
        data,
        columns: columns,
        state: {
            sorting,
            globalFilter,
            pagination,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        enableSorting: true,
        enableGlobalFilter: true,
        debugTable: false,
    });

    // Referencias para virtualización
    const tableContainerRef = useRef(null);
    const { rows } = table.getRowModel();

    // Configurar el virtualizador
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: 10,
    });

    // Calcular la altura total de la tabla
    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();
    const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
    const paddingBottom = virtualRows.length > 0
        ? totalSize - virtualRows[virtualRows.length - 1].end
        : 0;

    // Manejar el cambio en la búsqueda global
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            setGlobalFilter(value);
            if (onSearch) onSearch(value);
            setPagination(prev => ({ ...prev, pageIndex: 0 }));
        }, 400);
    };

    // Manejar cambio en el tamaño de página
    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPagination(prev => ({
            ...prev,
            pageSize: newSize,
        }));
        if (onPageSizeChange) onPageSizeChange(newSize);
    };

    // Descargar Reporte EXCEL 
    const handleExportToExcel = async () => {
        if (window['guardarBitacoraExport']) {
            try {
                window['guardarBitacoraExport']();
            } catch (error) {

            }
        }

        try {
            // Default export: exporta todos los datos filtrados
            const dataToExport = table.getFilteredRowModel().rows.map(row => {
                const baseData = {
                    Observaciones: row.original.observaciones,
                    Nombre_usuario: row.original.usuario,
                    Fecha: formatFecha(row.original.fecha, title),
                };

                // Solo incluir categoría si no se debe ocultar
                if (!shouldHideCategories) {
                    baseData.Categoria = toPascalCase(row.original.categoria);
                }

                return baseData;
            });

            //Columnas del reporte 
            let columns = [
                'Fecha',
                'Nombre Usuario',
                'Observaciones',
            ];

            // Solo agregar columna de categoría si no se debe ocultar
            if (!shouldHideCategories) {
                columns.push('Categoría');
            }

            // Agregar detalle 
            let dataReport = [];

            dataToExport.forEach((detalle, index) => {
                const rowData = [
                    detalle.Fecha?.toString() || "N/A",
                    detalle.Nombre_usuario?.toString() || "N/A",
                    detalle.Observaciones?.toString() || "N/A",
                ];

                // Solo agregar categoría si no se debe ocultar
                if (!shouldHideCategories) {
                    rowData.push(detalle.Categoria?.toString() || "N/A");
                }

                dataReport.push(rowData);
            });

            //Exportar reporte
            generateExcelReport('Reporte bitacora', dataReport, false, columns);

        } catch (error) {
            console.error("Error al exportar a Excel:", error);
        }
    };

    return (

        <Card className="h-100">
            <Card.Header>
                <Row className="d-flex align-items-center justify-content-between">
                    <Col md={6}>
                        <h6 className="mb-0 text-nowrap py-2 py-xl-0">{title}</h6>
                    </Col>
                    <Col md={4} className="d-flex justify-content-end align-items-center">
                        <Button
                            variant="primary"
                            size="sm"
                            className="me-2 p-2"
                            onClick={handleExportToExcel}
                            disabled={isExporting || rows.length === 0}
                        >
                            {isExporting ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-1"
                                    />
                                    Exportando...
                                </>
                            ) : (
                                <>
                                    <img
                                        src={iconoPng}
                                        alt="Exportar" title='Exportar a Excel'
                                        style={{ width: '20px', height: '20px', filter: 'brightness(0) invert(1)' }} // Ajusta el tamaño
                                        className="me-1 ms-1"
                                    />
                                    {/* <FaFileExport className="me-1 ms-1" /> */}
                                </>
                            )}
                        </Button>

                        <InputGroup size="sm" style={{ maxWidth: 200 }}>
                            {/* <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text> */}
                            <Form.Control
                                className='p-2'
                                type="text"
                                placeholder="Buscar..."
                                value={inputValue || ''}
                                onChange={handleSearchChange}
                            />
                            {globalFilter && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => {
                                        setGlobalFilter('');
                                        setInputValue(''); // <-- Asegura que el input se limpie visualmente
                                        if (onSearch) onSearch('');
                                    }}
                                >
                                    &times;
                                </Button>
                            )}
                        </InputGroup>
                    </Col>
                    <Col md={2}>

                        <Form className='d-flex p-2 flex-row-reverse'>
                            <Form.Select
                                className='p-2'
                                size="sm"
                                style={{ width: '100px' }}
                                value={table.getState().pagination.pageSize}
                                onChange={handlePageSizeChange}
                            >
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option value="250">250</option>
                                <option value="500">500</option>
                                <option value="1000">1000</option>
                            </Form.Select>
                        </Form>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body className="p-0">
                <style>{customStyles}</style>
                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <Spinner animation="border" role="status" variant="primary">
                            <span className="visually-hidden">Cargando...</span>
                        </Spinner>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="text-center p-5">No hay datos para mostrar</div>
                ) : (
                    <div style={{ overflow: 'auto' }}>
                        <div style={{ minWidth: 1100 }}>
                            {/* Cabecera de la tabla - fija */}
                            <div className="d-flex table-header p-3 ps-3 text-nowrap align-middle">
                                <div style={{ width: shouldHideCategories ? '50%' : '40%', marginLeft: '4px' }}>Observaciones</div>
                                <div style={{ width: shouldHideCategories ? '25%' : '20%' }}>Nombre usuario</div>
                                <div style={{ width: shouldHideCategories ? '25%' : '20%' }}>Fecha</div>
                                {!shouldHideCategories && <div style={{ width: '20%' }}>Categoría</div>}
                            </div>

                            {/* Contenedor de tabla con altura fija y scroll */}
                            <div
                                ref={tableContainerRef}
                                style={{
                                    height: 'calc(65vh - 40px)',
                                    overflow: 'auto',
                                }}
                                className="list-striped"
                            >
                                <div
                                    style={{
                                        height: `${totalSize}px`,
                                        width: '100%',
                                        position: 'relative',
                                    }}
                                >
                                    {virtualRows.map(virtualRow => {

                                        const row = rows[virtualRow.index];
                                        console.log(row);
                                        return (
                                            <div
                                                key={row.id}
                                                className="alert-row d-flex"
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: `${ROW_HEIGHT}px`,
                                                    transform: `translateY(${virtualRow.start}px)`,
                                                    alignItems: 'center',
                                                    borderBottom: '1px solid #f5f5f5',
                                                }}
                                            >
                                                {/* <div style={{ width: '15%', fontWeight: '500' }}>{formatFecha(row.original.fecha)}</div> */}

                                                <div style={{ width: shouldHideCategories ? '50%' : '40%', marginLeft: '12px', }}>
                                                    {(() => {
                                                        const observaciones = row.original.observaciones || '';
                                                        const texto = observaciones.length > 80 ? `${observaciones.slice(0, 80)}...` : observaciones;

                                                        return observaciones.length > 8 ? (
                                                            <OverlayTrigger
                                                                placement="bottom"
                                                                overlay={
                                                                    <Tooltip
                                                                        id={`tooltip-obs-${row.id || Math.random()}`}
                                                                        className="tooltip-adaptive"
                                                                    >
                                                                        {observaciones}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <span style={{ cursor: 'help' }}>
                                                                    {texto}
                                                                </span>
                                                            </OverlayTrigger>
                                                        ) : (
                                                            <span>{texto}</span>
                                                        );
                                                    })()}
                                                </div>
                                                <div style={{ width: shouldHideCategories ? '25%' : '20%' }}>{row.original.usuario || 'N/A'}</div>
                                                <div style={{ width: shouldHideCategories ? '25%' : '20%' }}>
                                                    {formatFecha(row.original.fecha, title)}  {/* ✅ Pasar title */}
                                                </div>
                                                {!shouldHideCategories && (
                                                    <div style={{ width: '20%' }}>
                                                        {toPascalCase(row.original.categoria)}
                                                    </div>
                                                )}

                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Card.Body>

            <Card.Footer className="bg-white">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <div className="d-flex align-items-center">
                        <span className="me-2">
                            Total: {table.getFilteredRowModel().rows.length} | Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
                        </span>
                        <Form.Control
                            type="number"
                            min="1"
                            max={table.getPageCount()}
                            value={table.getState().pagination.pageIndex + 1}
                            onChange={e => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                table.setPageIndex(page);
                            }}
                            style={{ width: '70px', height: '31px' }}
                            size="sm"
                        />
                    </div>
                    <div className="d-flex justify-content-center">
                        <Button
                            variant="outline-dark"
                            size="sm"
                            className="mx-1"
                            onClick={() => table.setPageIndex(0)}
                            disabled={!table.getCanPreviousPage()}
                        >
                            <FaAngleDoubleLeft />
                            {/* &laquo; Primera */}
                        </Button>
                        <Button
                            variant="outline-dark"
                            size="sm"
                            className="mx-1"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >
                            <FaAngleLeft />
                            {/* &lt; Anterior */}
                        </Button>

                        {/* Mostrar números de página (5 páginas alrededor de la actual) */}
                        {(() => {
                            // Determinar qué páginas mostrar
                            let pagesArray = [];
                            if (table.getPageCount() <= 5) {
                                // Si hay 5 o menos páginas, mostrarlas todas
                                pagesArray = Array.from({ length: table.getPageCount() }, (_, i) => i);
                            } else if (table.getState().pagination.pageIndex <= 2) {
                                // Si estamos en las primeras páginas
                                pagesArray = [0, 1, 2, 3, 4];
                            } else if (table.getState().pagination.pageIndex >= table.getPageCount() - 3) {
                                // Si estamos en las últimas páginas
                                pagesArray = [
                                    table.getPageCount() - 5,
                                    table.getPageCount() - 4,
                                    table.getPageCount() - 3,
                                    table.getPageCount() - 2,
                                    table.getPageCount() - 1
                                ];
                            } else {
                                // Estamos en medio, mostrar 2 antes y 2 después
                                pagesArray = [
                                    table.getState().pagination.pageIndex - 2,
                                    table.getState().pagination.pageIndex - 1,
                                    table.getState().pagination.pageIndex,
                                    table.getState().pagination.pageIndex + 1,
                                    table.getState().pagination.pageIndex + 2
                                ];
                            }

                            return pagesArray.map(pageNum => (
                                <Button
                                    key={`page-${pageNum}`}
                                    variant={pageNum === table.getState().pagination.pageIndex ? "primary" : "outline-dark"}
                                    size="sm"
                                    className="mx-1"
                                    onClick={() => table.setPageIndex(pageNum)}
                                >
                                    {pageNum + 1}
                                </Button>
                            ));
                        })()}

                        <Button
                            variant="outline-dark"
                            size="sm"
                            className="mx-1"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >
                            {/* Siguiente &gt; */}
                            <FaAngleRight />
                        </Button>
                        <Button
                            variant="outline-dark"
                            size="sm"
                            className="mx-1"
                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                            disabled={!table.getCanNextPage()}
                        >
                            {/* Última &raquo; */}
                            <FaAngleDoubleRight />
                        </Button>
                    </div>
                </div>
            </Card.Footer>
        </Card>

    );

}

const MasterTableBitacora = ({
    data = [],
    perPage = 10,
    title,
    isGroup
}) => {

    const safeTableData = Array.isArray(data) ? data : [];

    // Estructura de columnas 
    const columns = [
        {
            accessorKey: 'observaciones',
            header: 'Observaciones',
            cell: ({ row: { original } }) => {
                const observaciones = original.observaciones || '';
                const texto = observaciones.length > 100 ? `${observaciones.slice(0, 100)}...` : observaciones;

                return observaciones.length > 100 ? (
                    <OverlayTrigger
                        placement="bottom"
                        overlay={
                            <Tooltip
                                id={`tooltip-obs-${original.id || Math.random()}`}
                                className="tooltip-wide"
                            >
                                {observaciones}
                            </Tooltip>
                        }
                    >
                        <span style={{ cursor: 'help' }}>{texto}</span>
                    </OverlayTrigger>
                ) : (
                    <span>{texto}</span>
                );
            }
        },
        { accessorKey: 'usuario', header: 'Nombre Usuario', cell: ({ row: { original } }) => original.usuario },
        { accessorKey: 'fecha', header: 'Fecha', cell: ({ row: { original } }) => formatFecha(original.fecha) },
        { accessorKey: 'categoria', header: 'Categoría', cell: ({ row: { original } }) => toPascalCase(original.categoria) },
    ];


    const table = useReactTable({
        data: safeTableData,
        columns,
        sortable: true,
        pagination: true,
        perPage,
        getPaginationRowModel: getPaginationRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSubRows: (row) => row.children,
        enableColumnFilters: false
    });

    const Filter = function ({ column, table }) {
        const firstValue = table
            .getPreFilteredRowModel()
            .flatRows[0]?.getValue(column.id)
        const columnFilterValue = column.getFilterValue()

        return typeof firstValue === "number" ? (
            <div className="flex space-x-2">
                <input
                    type="number"
                    value={columnFilterValue?.[0] ?? ""}
                    onChange={e => column.setFilterValue(old => [e.target.value, old?.[1]])}
                    placeholder={`Min`}
                    className="w-24 border shadow rounded"
                />
            </div>
        ) : (
            <input
                type="text"
                value={columnFilterValue ?? ""}
                onChange={e => column.setFilterValue(e.target.value)}
                placeholder={`Search...`}
                className="w-36 border shadow rounded"
            />
        )
    }

    return (
        <Card className="h-100">

            <Card.Header>
            </Card.Header>

            <Card.Body>
                <>
                    <table className='fs-10 mb-0 overflow-hidden table table-striped table-bordered'>
                        <thead className='p-3 text-nowrap align-middle'>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map(header => {
                                        return (
                                            <th key={header.id} colSpan={header.colSpan}>
                                                {header.isPlaceholder ? null : (
                                                    <div>
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}

                                                        {header.column.getCanFilter() ? (
                                                            <div>
                                                                <Filter column={header.column} table={table} />
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                )}
                                            </th>
                                        )
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map(row => {
                                return (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map(cell => {
                                            return (
                                                <td key={cell.id}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </>
            </Card.Body>

            <Card.Footer className="bg-white">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <div className="d-flex align-items-center">
                        <span className="me-2">
                            Total: {table.getFilteredRowModel().rows.length} | Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
                        </span>
                        <Form.Control
                            type="number"
                            min="1"
                            max={table.getPageCount()}
                            value={table.getState().pagination.pageIndex + 1}
                            onChange={e => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                table.setPageIndex(page);
                            }}
                            style={{ width: '70px', height: '31px' }}
                            size="sm"
                        />
                    </div>

                    <div className="d-flex justify-content-center">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="mx-1"
                            onClick={() => table.setPageIndex(0)}
                            disabled={!table.getCanPreviousPage()}
                        >
                            &laquo; Primera
                        </Button>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="mx-1"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >
                            &lt; Anterior
                        </Button>

                        {/* Números de página (5 páginas alrededor de la actual) */}
                        {(() => {
                            let pagesArray = [];
                            if (table.getPageCount() <= 5) {
                                pagesArray = Array.from({ length: table.getPageCount() }, (_, i) => i);
                            } else if (table.getState().pagination.pageIndex <= 2) {
                                pagesArray = [0, 1, 2, 3, 4];
                            } else if (table.getState().pagination.pageIndex >= table.getPageCount() - 3) {
                                pagesArray = [
                                    table.getPageCount() - 5,
                                    table.getPageCount() - 4,
                                    table.getPageCount() - 3,
                                    table.getPageCount() - 2,
                                    table.getPageCount() - 1
                                ];
                            } else {
                                pagesArray = [
                                    table.getState().pagination.pageIndex - 2,
                                    table.getState().pagination.pageIndex - 1,
                                    table.getState().pagination.pageIndex,
                                    table.getState().pagination.pageIndex + 1,
                                    table.getState().pagination.pageIndex + 2
                                ];
                            }

                            return pagesArray.map(pageNum => (
                                <Button
                                    key={`page-${pageNum}`}
                                    variant={pageNum === table.getState().pagination.pageIndex ? "primary" : "outline-primary"}
                                    size="sm"
                                    className="mx-1"
                                    onClick={() => table.setPageIndex(pageNum)}
                                >
                                    {pageNum + 1}
                                </Button>
                            ));
                        })()}

                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="mx-1"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >
                            Siguiente &gt;
                        </Button>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="mx-1"
                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                            disabled={!table.getCanNextPage()}
                        >
                            Última &raquo;
                        </Button>
                    </div>

                    <div>
                        <Form.Select
                            size="sm"
                            style={{ width: '120px' }}
                            value={table.getState().pagination.pageSize}
                            onChange={e => table.setPageSize(Number(e.target.value))}
                        >
                            <option value="50">50 por pág.</option>
                            <option value="100">100 por pág.</option>
                            <option value="250">250 por pág.</option>
                            <option value="500">500 por pág.</option>
                            <option value="1000">1000 por pág.</option>
                        </Form.Select>
                    </div>
                </div>
            </Card.Footer>
        </Card>
    );
};

export default MasterTableBitacora;
export { BitacoraTableListTanstack };